import { Container, Graphics, Sprite, Texture } from 'pixi.js'
import { TERRAIN, getTerrainById, isWalkable, isBuildable } from '../data/terrain'
import { octaveNoise, SeededRandom } from '../utils/math'
import { PathGrid, findPath as astarFindPath } from '../utils/pathfinding'
import { AssetLoader } from '../engine/AssetLoader'

export interface MapConfig {
  width: number
  height: number
  seed: number
  tileSize: number
}

export class GameMap {
  width: number
  height: number
  seed: number
  tileSize: number

  terrain: number[][]  // terrain IDs
  fogOfWar: number[][] // 0 = unexplored, 1 = explored, 2 = visible
  buildingOccupancy: boolean[][]  // true if tile has a building

  private terrainContainer: Container
  private terrainSprites: Map<string, Sprite>  // key: "x,y"
  private fogGraphics: Graphics
  private gridGraphics: Graphics

  constructor(config: MapConfig) {
    this.width = config.width
    this.height = config.height
    this.seed = config.seed
    this.tileSize = config.tileSize

    this.terrain = []
    this.fogOfWar = []
    this.buildingOccupancy = []

    this.terrainContainer = new Container()
    this.terrainSprites = new Map()
    this.fogGraphics = new Graphics()
    this.gridGraphics = new Graphics()

    this.generate()
  }

  generate(): void {
    const rand = new SeededRandom(this.seed)

    // Initialize arrays
    for (let y = 0; y < this.height; y++) {
      this.terrain[y] = []
      this.fogOfWar[y] = []
      this.buildingOccupancy[y] = []

      for (let x = 0; x < this.width; x++) {
        this.fogOfWar[y][x] = 0
        this.buildingOccupancy[y][x] = false

        // Generate terrain using noise
        const noiseX = (x + 50) / 15
        const noiseY = (y + 50) / 15
        let elevation = octaveNoise(noiseX, noiseY, 4, 0.5, this.seed)
        const moisture = octaveNoise((x + 50) / 20, (y + 50) / 20, 3, 0.5, this.seed + 100)
        const resources = octaveNoise((x + 50) / 10, (y + 50) / 10, 2, 0.6, this.seed + 200)

        // Distance from center of map - use to create more interesting terrain
        const centerX = this.width / 2
        const centerY = this.height / 2
        const distFromCenter = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2))
        const maxDist = Math.sqrt(centerX * centerX + centerY * centerY)

        // Water only appears in pockets away from edges - use a very high threshold
        // and require both low elevation AND high moisture for water
        const isWater = elevation < 0.12 && moisture > 0.55 && distFromCenter < maxDist * 0.7

        let terrainId: number

        if (isWater) {
          terrainId = TERRAIN.WATER.id
        } else if (elevation > 0.82) {
          terrainId = TERRAIN.MOUNTAIN.id
        } else if (moisture > 0.6 && resources > 0.5) {
          terrainId = TERRAIN.FOREST.id
        } else if (resources > 0.7) {
          terrainId = rand.next() > 0.5 ? TERRAIN.COMPUTE_NODE.id : TERRAIN.ENERGY_WELL.id
        } else {
          terrainId = TERRAIN.GRASS.id
        }

        this.terrain[y][x] = terrainId
      }
    }

    // Clear starting areas (corners) for player bases - large areas to ensure playable start
    console.log('Clearing starting areas...')
    this.clearArea(2, 2, 22)  // Top-left (player) - bigger area
    this.clearArea(this.width - 24, this.height - 24, 22)  // Bottom-right (enemy) - bigger area

    // Clear a wide diagonal path connecting the two bases directly
    this.clearPath(12, 12, this.width - 12, this.height - 12, 6)  // Diagonal across entire map

    // Also clear paths along edges as backup routes
    this.clearPath(12, 12, this.width - 12, 12, 4)  // Top edge
    this.clearPath(12, 12, 12, this.height - 12, 4)  // Left edge

    // DEBUG: Count terrain types after clearing
    let waterCount = 0, grassCount = 0
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.terrain[y][x] === TERRAIN.WATER.id) waterCount++
        if (this.terrain[y][x] === TERRAIN.GRASS.id) grassCount++
      }
    }
    console.log(`Terrain after clearing: ${waterCount} water tiles, ${grassCount} grass tiles`)
  }

  private clearArea(startX: number, startY: number, size: number): void {
    for (let y = startY; y < startY + size && y < this.height; y++) {
      for (let x = startX; x < startX + size && x < this.width; x++) {
        if (x >= 0 && y >= 0) {
          this.terrain[y][x] = TERRAIN.GRASS.id
        }
      }
    }
  }

  // Clear a path between two points to ensure connectivity
  private clearPath(x1: number, y1: number, x2: number, y2: number, width: number): void {
    const dx = x2 - x1
    const dy = y2 - y1
    const steps = Math.max(Math.abs(dx), Math.abs(dy))

    for (let i = 0; i <= steps; i++) {
      const t = steps === 0 ? 0 : i / steps
      const cx = Math.floor(x1 + dx * t)
      const cy = Math.floor(y1 + dy * t)

      // Clear a square around this point
      for (let oy = -width; oy <= width; oy++) {
        for (let ox = -width; ox <= width; ox++) {
          const x = cx + ox
          const y = cy + oy
          if (x >= 0 && x < this.width && y >= 0 && y < this.height) {
            this.terrain[y][x] = TERRAIN.GRASS.id
          }
        }
      }
    }
  }

  // Rendering
  renderTerrain(container: Container, visibleBounds: { minX: number; minY: number; maxX: number; maxY: number }): void {
    const { minX, minY, maxX, maxY } = visibleBounds

    // Track which sprites should be visible
    const visibleKeys = new Set<string>()

    for (let y = Math.max(0, minY); y < Math.min(this.height, maxY); y++) {
      for (let x = Math.max(0, minX); x < Math.min(this.width, maxX); x++) {
        const key = `${x},${y}`
        visibleKeys.add(key)

        const terrainId = this.terrain[y][x]
        const terrain = getTerrainById(terrainId)
        if (!terrain) continue

        // Get or create sprite for this tile
        let sprite = this.terrainSprites.get(key)
        if (!sprite) {
          const texture = this.getTerrainTextureForId(terrainId)
          if (texture) {
            sprite = new Sprite(texture)
            sprite.width = this.tileSize
            sprite.height = this.tileSize
            sprite.x = x * this.tileSize
            sprite.y = y * this.tileSize
            this.terrainSprites.set(key, sprite)
            this.terrainContainer.addChild(sprite)
          }
        }

        // Make sure sprite is visible
        if (sprite) {
          sprite.visible = true
        }
      }
    }

    // Hide sprites outside visible bounds
    for (const [key, sprite] of this.terrainSprites.entries()) {
      if (!visibleKeys.has(key)) {
        sprite.visible = false
      }
    }

    if (!this.terrainContainer.parent) {
      container.addChild(this.terrainContainer)
    }
  }

  private getTerrainTextureForId(terrainId: number): Texture | null {
    return AssetLoader.getTerrainTexture(terrainId)
  }

  renderGrid(container: Container, visibleBounds: { minX: number; minY: number; maxX: number; maxY: number }): void {
    this.gridGraphics.clear()
    this.gridGraphics.setStrokeStyle({ width: 1, color: 0xffffff, alpha: 0.05 })

    const { minX, minY, maxX, maxY } = visibleBounds

    for (let y = Math.max(0, minY); y < Math.min(this.height, maxY); y++) {
      for (let x = Math.max(0, minX); x < Math.min(this.width, maxX); x++) {
        const screenX = x * this.tileSize
        const screenY = y * this.tileSize
        this.gridGraphics.rect(screenX, screenY, this.tileSize, this.tileSize)
        this.gridGraphics.stroke()
      }
    }

    if (!this.gridGraphics.parent) {
      container.addChild(this.gridGraphics)
    }
  }

  renderFog(container: Container, visibleBounds: { minX: number; minY: number; maxX: number; maxY: number }): void {
    this.fogGraphics.clear()

    const { minX, minY, maxX, maxY } = visibleBounds

    for (let y = Math.max(0, minY); y < Math.min(this.height, maxY); y++) {
      for (let x = Math.max(0, minX); x < Math.min(this.width, maxX); x++) {
        const fog = this.fogOfWar[y][x]
        if (fog === 2) continue  // Fully visible

        const screenX = x * this.tileSize
        const screenY = y * this.tileSize

        const alpha = fog === 0 ? 0.9 : 0.5
        this.fogGraphics.rect(screenX, screenY, this.tileSize, this.tileSize)
        this.fogGraphics.fill({ color: 0x000000, alpha })
      }
    }

    if (!this.fogGraphics.parent) {
      container.addChild(this.fogGraphics)
    }
  }

  // Fog of war
  revealArea(centerX: number, centerY: number, radius: number): void {
    const tileX = Math.floor(centerX / this.tileSize)
    const tileY = Math.floor(centerY / this.tileSize)

    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const x = tileX + dx
        const y = tileY + dy

        if (x < 0 || x >= this.width || y < 0 || y >= this.height) continue

        // Circle check
        if (dx * dx + dy * dy <= radius * radius) {
          this.fogOfWar[y][x] = 2  // Currently visible
        }
      }
    }
  }

  // Reset visibility but keep explored
  resetVisibility(): void {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.fogOfWar[y][x] === 2) {
          this.fogOfWar[y][x] = 1  // Explored but not visible
        }
      }
    }
  }

  // Pathfinding
  getPathGrid(): PathGrid {
    return {
      width: this.width,
      height: this.height,
      isWalkable: (x: number, y: number) => {
        if (x < 0 || x >= this.width || y < 0 || y >= this.height) return false
        if (this.buildingOccupancy[y][x]) return false
        return isWalkable(this.terrain[y][x])
      },
      getCost: (x: number, y: number) => {
        const terrain = getTerrainById(this.terrain[y][x])
        return terrain?.movementCost ?? 1
      }
    }
  }

  findPath(startX: number, startY: number, endX: number, endY: number): { x: number; y: number }[] {
    const grid = this.getPathGrid()
    return astarFindPath(
      { x: startX / this.tileSize, y: startY / this.tileSize },
      { x: endX / this.tileSize, y: endY / this.tileSize },
      grid
    ).map(p => ({
      x: p.x * this.tileSize,
      y: p.y * this.tileSize
    }))
  }

  // Building placement
  canPlaceBuilding(tileX: number, tileY: number, size: number, requiresTerrain?: number): boolean {
    for (let dy = 0; dy < size; dy++) {
      for (let dx = 0; dx < size; dx++) {
        const x = tileX + dx
        const y = tileY + dy

        if (x < 0 || x >= this.width || y < 0 || y >= this.height) return false
        if (this.buildingOccupancy[y][x]) return false
        if (!isBuildable(this.terrain[y][x])) return false
      }
    }

    // Check terrain requirement
    if (requiresTerrain !== undefined) {
      let hasRequired = false
      for (let dy = 0; dy < size; dy++) {
        for (let dx = 0; dx < size; dx++) {
          if (this.terrain[tileY + dy][tileX + dx] === requiresTerrain) {
            hasRequired = true
            break
          }
        }
        if (hasRequired) break
      }
      if (!hasRequired) return false
    }

    return true
  }

  occupyTiles(tileX: number, tileY: number, size: number): void {
    for (let dy = 0; dy < size; dy++) {
      for (let dx = 0; dx < size; dx++) {
        this.buildingOccupancy[tileY + dy][tileX + dx] = true
      }
    }
  }

  freeTiles(tileX: number, tileY: number, size: number): void {
    for (let dy = 0; dy < size; dy++) {
      for (let dx = 0; dx < size; dx++) {
        this.buildingOccupancy[tileY + dy][tileX + dx] = false
      }
    }
  }

  // Get terrain at world position
  getTerrainAt(worldX: number, worldY: number): number {
    const tileX = Math.floor(worldX / this.tileSize)
    const tileY = Math.floor(worldY / this.tileSize)

    if (tileX < 0 || tileX >= this.width || tileY < 0 || tileY >= this.height) {
      return -1
    }

    return this.terrain[tileY][tileX]
  }

  isVisible(worldX: number, worldY: number): boolean {
    const tileX = Math.floor(worldX / this.tileSize)
    const tileY = Math.floor(worldY / this.tileSize)

    if (tileX < 0 || tileX >= this.width || tileY < 0 || tileY >= this.height) {
      return false
    }

    return this.fogOfWar[tileY][tileX] === 2
  }

  isExplored(worldX: number, worldY: number): boolean {
    const tileX = Math.floor(worldX / this.tileSize)
    const tileY = Math.floor(worldY / this.tileSize)

    if (tileX < 0 || tileX >= this.width || tileY < 0 || tileY >= this.height) {
      return false
    }

    return this.fogOfWar[tileY][tileX] >= 1
  }

  destroy(): void {
    // Clean up all terrain sprites
    for (const sprite of this.terrainSprites.values()) {
      sprite.destroy()
    }
    this.terrainSprites.clear()
    this.terrainContainer.destroy()
    this.fogGraphics.destroy()
    this.gridGraphics.destroy()
  }
}
